import { BlogPost } from './types';

export const snowboardsPosts: BlogPost[] = [
  {
    id: "from-rentals-to-doa",
    title: "From Rentals to the Capita DOA: My First Season Progression",
    excerpt: "How demoing the Capita DOA at Palisades Tahoe turned a beginner into a confident snowboarder.",
    content: `Slush and lots of sunscreen.

It was late spring, 2024, at Palisades Tahoe, and I was catching the last of the last of the season.

I'd gotten the Spring Trio + Demo package, and I was in the market for my own board. Up till then, I had only rented because I was brand new to the sport.

My first day at Palisades (formerly Squaw Valley or "Squawlywood"), I opted for the LibTech Orca. A great, beginner-friendly board that I had no problems with. However, I needed to be pushed a little more. On the second day, I asked the shop attendant to recommend something a little more challenging. Something that would encourage me to progress, but not too strongly.

"Ah," he said confidently. "I know what you need. I'm gonna set you up with a Capita DOA."

"Um, ok," I said like a noob. The guy could've said I should ride a screen door, and I probably would've done it, but what he gave me was magic.

"Yes," I thought to myself as I rode down Poppy and Monkey Flower repeatedly. "This board feels great." Not too squirrely but a little more challenging than the Orca. So when it came time for the 2024/25 season to kick off, I knew what to order.

I found my way to [Evo.com](https://www.evo.com/), where I found a great deal on a full setup — a Capita DOA 158, Season bindings, a Giro helmet, and K2 boots. Btw, NEVER buy boots online before trying them on. Luckily, I can make the K2s work by doubling up thick socks, but thinking my size 10 street shoe size would fit me was a mistake.

By the end of the 24/25 season, I was charging black runs and hitting the park regularly on the DOA after starting the season on the bunny slope. Getting acclimated to the board took some time, but once I learned edge control and how to carve, my progression really took off.

What allowed me to have such a successful first season was that I tried a board, found out what worked, and bought the same board. In other words, I demoed before I purchased, and this is why I created DemoStoke. I want people to have that same experience and make it as easy as possible.

So the next time you're in the market for a new board (or boots!), try before you buy. This applies to surfboards, skis, bikes, or any other equipment. You can't replicate the knowledge of what it *feels* like to ride a piece of gear. No stats, AI, or seeing what your friend rides can replace putting something through its paces in different conditions. You have to get out there and do it.

Contact me and share a story about a success or failure you've experienced with gear purchases!

Cheers 🤙🏻`,
    category: "snowboards",
    publishedAt: "2025-07-03",
    readTime: 3,
    heroImage: "https://qtlhqsqanbxgfbcjigrl.supabase.co/storage/v1/object/public/blog-images//admin-image-1751601812768-Capita%20DP%20Cropped.jpg",
    thumbnail: "https://qtlhqsqanbxgfbcjigrl.supabase.co/storage/v1/object/public/blog-images//admin-image-1751601805697-Capita%20Against%20Rail.jpg",
    tags: ["demo gear", "capita", "progression"],
    author: "Michael Zick",
    authorId: "98f914a6-2a72-455d-aa4b-41b081f4014d"
  },
  {
    id: "snowboard-buying-guide-2025",
    title: "The Ultimate Snowboard Buying Guide for 2025: Everything You Need to Know",
    excerpt: "From powder boards to park sticks, discover how to choose the perfect snowboard for your riding style, skill level, and the terrain you love most.",
    content: `Choosing the right snowboard can make or break your mountain experience. Whether you're a first-time buyer or looking to upgrade your quiver, this comprehensive guide will help you find the perfect board for your needs.

**Understanding Snowboard Types**

**All-Mountain Boards:**

- The most versatile option, perfect for riders who want to explore everything the mountain offers.
- These boards typically feature a directional twin shape with medium flex, making them ideal for groomed runs, light powder, and occasional park sessions.

**Powder Boards:**

- Designed specifically for deep snow conditions, these boards feature longer noses, setback stances, and often incorporate rocker profiles.
- They excel in untouched powder but can feel cumbersome on groomed terrain.

**Freestyle/Park Boards:**

- Built for jumps, rails, and creative riding, these boards are typically shorter, softer, and feature true twin shapes for switch riding.
- They prioritize maneuverability over speed and stability.

**Board Profile Basics**

**Camber:**

- The traditional snowboard profile with an arch between the contact points.
- Provides excellent edge hold and pop but can be less forgiving for beginners.

**Rocker (Reverse Camber):**

- The opposite of camber, with the center of the board touching the snow.
- Offers easier turn initiation and better float in powder but sacrifices some edge hold on hard snow.

**Hybrid Profiles:**

- Combinations of camber and rocker designed to optimize performance for specific riding styles.
- Popular options include camber-rocker-camber and rocker-camber-rocker configurations.

**Sizing Your Snowboard**

**Shorter boards (chest height):**

- Easier to maneuver, better for park and freestyle.

**Medium boards (chin to nose):**

- Versatile for all-mountain riding.

**Longer boards (above nose):**

- Better float in powder, more stability at speed.

**Boot and Binding Compatibility**

- Ensure your boots and bindings are compatible with your chosen board.
- Most snowboards use standard mounting patterns, but some high-end boards feature proprietary systems that require specific bindings.

**Setting Your Budget**

**Entry-level boards ($200-400):**

- Perfect for beginners learning the basics.

**Mid-range boards ($400-600):**

- Quality construction for intermediate riders.

**High-end boards ($600+):**

- Premium materials and technology for advanced riders.

**Top Picks for 2025**

**Beginner:**

- Burton Ripcord - Forgiving flex and stable platform.

**Intermediate:**

- Capita Defenders of Awesome - Versatile all-mountain performance.

**Advanced:**

- Jones Flagship - Premium construction for serious mountain exploration.

**Powder:**

- Lib Tech Orca - Short and wide design for effortless powder surfing.

**Park:**

- Salomon Assassin - Soft flex and true twin shape for freestyle progression.

**Making Your Decision**

- Consider where you'll be riding most often.
- If you primarily ski groomed runs at your local mountain, prioritize edge hold and carving performance.
- If you chase powder days, look for boards with setback stances and generous nose lengths.

**Maintenance Tips**

- Wax your board regularly for optimal performance.
- Check edges for damage after each session.
- Store your board in a cool, dry place during off-season.
- Professional tune-ups at the start of each season.

The perfect snowboard is the one that matches your riding style and helps you progress. Take time to demo different boards when possible, and don't hesitate to ask shop experts for advice based on your specific needs.

**Advanced Snowboard Techniques**

**1. Dynamic Edge Control**

- Moving beyond basic carving, dynamic edge control involves actively managing your edge engagement throughout the turn.
- This technique allows for precise speed control and superior performance on varying snow conditions.

**Practice Method:**

- Start on gentle slopes and focus on gradually increasing and decreasing edge angle throughout your turns.
- Feel how subtle changes in pressure affect your speed and turn radius.

**Key Points:**

- Use your ankles and knees to fine-tune edge angle.
- Maintain consistent upper body position.
- Practice both heelside and toeside edge control equally.

**2. Counter-Rotation Technique**

- Counter-rotation involves rotating your upper and lower body in opposite directions to initiate turns more efficiently.
- This advanced technique is essential for riding steep terrain and tight trees.

**Practice Method:**

- Begin on moderate slopes, focusing on keeping your shoulders facing downhill while your legs and board turn across the fall line.
- Start with gentle counter-rotation and gradually increase the movement.

**Benefits:**

- Faster turn initiation.
- Better control in steep terrain.
- Reduced upper body movement.
- Improved balance and stability.

**3. Terrain Reading and Line Selection**

- Advanced riders must learn to read terrain and select optimal lines before they reach challenging sections.
- This skill becomes crucial for off-piste riding and navigating complex terrain.

**Development Strategy:**

- Study terrain from chairlifts and safe vantage points.
- Identify potential hazards and optimal routes.
- Practice visualizing your line before dropping in.
- Start with familiar terrain and gradually tackle more complex areas.

**4. Switch Riding Mastery**

- True snowboard mastery requires comfortable switch (opposite stance) riding.
- This skill opens up endless possibilities for creative riding and is essential for freestyle progression.

**Training Progression:**

- Start with basic switch riding on gentle slopes.
- Practice switch carving and speed control.
- Work on switch jumps and basic tricks.
- Gradually increase terrain difficulty while riding switch.

**5. Advanced Powder Techniques**

- Deep powder requires specific techniques that differ significantly from groomed snow riding.
- Mastering these skills will transform your powder days from survival sessions to pure joy.

**Essential Powder Skills:**

- Weight distribution and stance adjustments.
- Rhythm and flow through deep snow.
- Using terrain features to maintain speed.
- Proper breathing and relaxation techniques.

**Building Your Skills Progressively**

- Don't attempt to master all these techniques at once.
- Focus on one skill at a time, practicing it consistently until it becomes natural.
- Each technique builds upon fundamental skills, so ensure your basics are solid before progressing.

**Practice Recommendations**

- Dedicate specific runs to technique practice.
- Film yourself to analyze your progress.
- Take lessons with certified instructors for feedback.
- Practice in various snow conditions and terrain.

**Safety Considerations**

- As you push your limits with advanced techniques, safety becomes even more critical:
- Always ride within your ability level.
- Inform others of your riding plans.
- Carry appropriate safety equipment for your terrain.
- Check weather and avalanche conditions.

**Mental Aspects**

- Advanced snowboarding is as much mental as it is physical. Develop confidence through:
- Visualization exercises.
- Progressive skill building.
- Positive self-talk.
- Learning from mistakes without fear.

Remember, progression in snowboarding is rarely linear. Some days will feel like breakthroughs, while others might feel like steps backward. Stay patient, keep practicing, and enjoy the journey of constant improvement.

The key to mastering these advanced techniques is consistent practice and patience. Focus on quality over quantity, and don't be afraid to go back to basics when needed. Each technique will enhance your overall riding ability and open up new possibilities on the mountain.
`,
    category: "snowboards",
    publishedAt: "2025-01-12",
    readTime: 10,
    heroImage: "https://neversummer.com/cdn/shop/articles/10TipsBlog.jpg?v=1653255663",
    thumbnail: "https://neversummer.com/cdn/shop/articles/10TipsBlog.jpg?v=1653255663",
    tags: ["snowboards", "advanced", "technique", "skills", "progression", "guides"],
    author: "Chad G.",
    authorId: "chad-g"
  }
];
